import { CartContext, CartProvider } from '@contexts/CartContext';
import { markCheckoutDeparture } from '@lib/commerce/cartCount';
import { useCartCount } from '@lib/commerce/useCartCount';
import { useMultiCart } from '@lib/flags/useMultiCart';
import { HeaderFastActionItem } from '@lib/types/cms/Header';
import { c } from '@ui-components/utils/tailwind-merge';
import { ForwardedRef, PropsWithChildren, useContext } from 'react';
import { IconWithCount } from 'ui-components';
import { IconMenuOption } from '../Header/IconMenuOption';

type FastActionMenuCartProps = {
  item: HeaderFastActionItem;
  forwardedRef: ForwardedRef<HTMLDivElement>;
  referenceProps: {};
  /** Merged onto the cart link's own classes. */
  className?: string;
};

type FastActionMenuCartLinkProps = FastActionMenuCartProps & {
  /** `undefined` hides the badge and leaves the count out of the label. */
  itemCount: number | undefined;
};

const FastActionMenuCartLink = ({
  item,
  forwardedRef,
  referenceProps,
  className,
  itemCount,
}: FastActionMenuCartLinkProps) => {
  // TODO: Translate the text
  const cartAriaLabel =
    itemCount === undefined
      ? `View ${item.topLevelLink?.value}`
      : `View ${item.topLevelLink?.value} (${itemCount} item${itemCount === 1 ? '' : 's'})`;
  return (
    <IconMenuOption
      name={cartAriaLabel}
      href={item.topLevelLink?.url || ''}
      ref={forwardedRef}
      {...referenceProps}
      className={c('relative rounded-full', className)}
      target={item.topLevelLink?.openInNewTab ? '_blank' : undefined}
    >
      <IconWithCount
        iconName={item.topLevelLink?.icon || ''}
        iconSize={20}
        count={itemCount ?? 0}
      />
    </IconMenuOption>
  );
};

/** Today's ASv2 count, unchanged while MULTI_CART is off. */
const Asv2FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { cart } = useContext(CartContext);
  return <FastActionMenuCartLink {...props} itemCount={cart?.ItemCount || 0} />;
};

/** CPS-7694: read-only commerce cart count, hidden while rate limited. */
const MultiCartFastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { count } = useCartCount();
  return (
    <span
      onClickCapture={markCheckoutDeparture}
      onAuxClickCapture={(event) => {
        if (event.button === 1) markCheckoutDeparture();
      }}
    >
      <FastActionMenuCartLink {...props} itemCount={count} />
    </span>
  );
};

export const FastActionMenuCart = (props: FastActionMenuCartProps) => {
  const { enabled } = useMultiCart();
  return enabled ? (
    <MultiCartFastActionMenuCart {...props} />
  ) : (
    <Asv2FastActionMenuCart {...props} />
  );
};

/**
 * Mounts the legacy ASv2 CartProvider only while MULTI_CART is off: its
 * `/api/carts` read would otherwise still run (and mint a device for a
 * cookieless guest) even though the badge no longer reads it.
 */
export const FastActionMenuCartProvider = ({ children }: PropsWithChildren) => {
  const { enabled } = useMultiCart();
  return enabled ? <>{children}</> : <CartProvider>{children}</CartProvider>;
};
